import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { richTextResolver } from '@storyblok/richtext';
import Client, { ISbStory } from 'storyblok-js-client';
import { DomSanitizer } from '@angular/platform-browser';
import { WINDOW } from './WindowService';
import { GlobalService } from './GlobalService';
import { Language, UrlParameters } from 'src/_model/shared';
import { CMSLink, CmsStory } from 'src/_model/storyblock';
import { isPlatformBrowser } from '@angular/common';

const { render } = richTextResolver()

@Injectable({ providedIn: 'root' })
export class CmsService {

  private storyLinks: CMSLink[] = [];
  private storyReferences: CmsStory[] = [];

  private sbClient = new Client({
    // accessToken: 'eIlcIWiyDRFlyuQQFTrKXgtt' // Add your token here
    accessToken: '0zrgJZNmDQLYVRO28fnqKQtt'
  });


  constructor(
    private sanitizer: DomSanitizer,
    private _globalService: GlobalService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(WINDOW) private window: Window,
  ) { }

  getStory(slug: string, version: string = null, resolve_links = "link", language: string = null): Promise<{ cv: number; links: any[]; rels: CmsStory[]; story: CmsStory; }> {

    if (!version) {
      const isDraft = isPlatformBrowser(this.platformId) && this.window.location && (this.window.location.href.includes('localhost') || this.window.location.href.includes('tst.ecom') || this.window.location.href.includes('cms'));
      version = isDraft ? 'draft' : 'published';
    }

    if (!language)
      language = this._globalService.getLanguage();

    // this is only needed ir you are in the SB live editor !?
    // debugger;
    const path = slug.split('/');
    if (path.length > 2 && !['general', 'global'].includes(path[1])) { // something like /fr/slug
      language = path[1];
      slug = '/' + path.slice(2).join('/'); // do we need the leading / ?
    }
    if (!!language) {
      if (language === 'fr') {
        // TODO: fix this depending on the settings in SB 
        language = 'fr-be';
      }
      if (language === 'nl') {
        // TODO: find out the main domain using a setting or config ?
        //!!this.window && !!this.window.location && this.window.location.hostname.split('.').slice(-1)[0] == 'be'
        if (this._globalService.getTld() === 'be') {
          language = 'nl-be';
        } else {
          // if we don't specify a lang we get the fallback versio
          // of the version that is associated with the translated slug

          // delete params['language'];
        }
      }
    }

    const params = new UrlParameters();
    params.addParameter('version', version)
    params.addParameter('resolve_links', resolve_links)
    params.addParameter('language', language)
    params.addParameter('resolve_relations', "global_reference.reference")

    return this.sbClient.getStory(slug, params.toObject()).then(res => {
      this.storyLinks = [...res.data.links as unknown as CMSLink[], ...this.storyLinks];
      this.storyReferences = [...res.data.rels as unknown as CmsStory[], ...this.storyReferences]
      return res.data as unknown as { cv: number; links: any[]; rels: CmsStory[]; story: CmsStory; };

    });
  }

  getStories(params?: object): Promise<any> {
    return this.sbClient.getStories(params).then(res => res.data);
  }

  getHrefForLanguage(id: string, language: Language = null): string {

    if (!language)
      language = Language[this._globalService.getLanguage().toUpperCase()]

    let link = this.storyLinks.find(s => s.uuid === id);
    if (!link) return null;

    let realPath = link.real_path || link.default_full_slug || link.full_slug || link.slug;

    //adds -be if you are on the .be website
    let langCode = `nl${this._globalService.getTld() === 'be' ? '-be' : ''}`
    switch (language) {
      case Language.EN:
        langCode = 'en'
        break;
      case Language.FR:
        langCode = 'fr-be'
        break;
    }

    //just return the path if it is dutch
    if (langCode === 'nl') return realPath

    //gets the alternate and returns the /langcode/url when it isnt found
    const alternate = link.alternates.find(a => a.lang === langCode)
    if (!alternate) return `/${langCode}${realPath}`

    //removes the starting / to compare to the alternate paths and adds the languagecode if they are the same
    if (realPath.startsWith("/")) realPath = realPath.substring(1, realPath.length);
    if (realPath === alternate.path) return `/${langCode}/${realPath}`

    //otherwise just return the path
    return alternate.path;

  }

  getReference(uuid:string): CmsStory{
    return this.storyReferences.find(r=> r.uuid === uuid)
  }

  render_rt(rt_item) {
    return this.get_sanitized_html(render(rt_item));
    //return this.sbClient.richTextResolver.render(rt_item);
  }

  get_src_as_css(field, contain, height?) {
    if (height) {
      return `url(${this.get_src(field, contain, height)}/m/)`;
    } else {
      return `url(${this.get_src(field, contain)}/m/)`;
    }
  }

  get_src(field, contain, height?, width?) {
      
    let compress = '';
    if (!!field?.filename?.includes('.svg')) {
      compress = '';
    } else if (contain) {
      // contain image, so only optimize, dont resize
      compress = '/m/';
    } else if (height && width) {
      compress = `/m/${width}x${height}`;
    } else if (height) {
      compress = `/m/0x${height}`;
    } else if (width) {
      compress = `/m/${width}x0`;
    } else {
      compress = '/m/800x0';
    }
    if (field.filename) return field.filename + compress;
    if (field.url) return field.url + compress;
    return '/missing_image.png'
  }

  get_alt(field) {
    if (field.alt) return field.alt;
    if (field.title) return field.title;
    return 'no alt or title set in CMS';
  }

  get_direction(section) {
    let flexProp = " md:flex-row";
    if (!!section.layout) {
      switch (section.layout) {
        case 'IMG_LEFT':
          flexProp = " md:flex-row-reverse";
          break;
        case 'IMG_CONNECTED_LEFT':
          flexProp = " md:flex-row-reverse";
          break;
        case 'AFBEELDING_LEFT':
          flexProp = " md:flex-row-reverse";
          break;
        default:
          break;
      }
    }
    return flexProp
  }

  get_content(field) {
    if (!field || field.length === 0) return null;
    // abstract field render can be used for content from different (CMS) sources
    // if (Array.isArray(field) && field[0].text) return field[0].text; // prismic style
    if (!!field.text) return field.text; // sb style
    // no extra work need
    return field;
  }

  get_sanitized_url(url) {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  get_sanitized_html(html) {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  get_href(field) {
    if (!field || field.length === 0) return null;
    // abstract field render can be used for content from different (CMS) sources
    // if (Array.isArray(field) && field[0].text) return field[0].text; // prismic style
    if (!!field.text) return field.text; // sb style
    // no extra work need
    return field;
  }

  get_classes(block) {
    let classes = '';
    if (block.rounded) {
      classes = classes + "rounded-2xl";
    }
    if (!!block.bgcolor) {
      classes = classes + ' bg-' + block.bgcolor;
      if (block.bgcolor.includes('darkgreen')) {
        classes = classes + " text-e-white";
      }
    }
    return classes;
  }
}
